import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
  getCamerasListService,
  getCameraStatusService,
  getCameraPictureService,
  revokeImageUrl,
} from "modules/monitoreo/services/camaraService";
import { matchesCamera } from "../model/cameraModel";

export const useCamaras = () => {
  const [comercios, setComercios] = useState([]);
  const [comercioSeleccionado, setComercioSeleccionado] = useState(null);
  const [camaraSeleccionada, setCamaraSeleccionada] = useState(null);
  const [detalleCamara, setDetalleCamara] = useState(null);
  const [modalAbierto, setModalAbierto] = useState(false);
  const [imagenCamara, setImagenCamara] = useState(null);
  const [cargandoImagen, setCargandoImagen] = useState(false);
  const [errorImagen, setErrorImagen] = useState(null);
  const [loading, setLoading] = useState({ initial: true, cameraStatus: false });
  const [error, setError] = useState(null);
  const [errorConsulta, setErrorConsulta] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [filtros, setFiltros] = useState({ busqueda: "", comercio: null, estado: "todos", ip: "" });
  const mounted = useRef(false);
  const statusRevision = useRef(0);
  const imageRevision = useRef(0);
  const listRevision = useRef(0);
  const imageUrl = useRef(null);
  const catalog = useRef([]);
  const activeStatus = useRef(null);
  const activeImage = useRef(null);

  const limpiarImagen = useCallback(() => {
    imageRevision.current += 1;
    activeImage.current = null;
    revokeImageUrl(imageUrl.current);
    imageUrl.current = null;
    setImagenCamara(null);
    setErrorImagen(null);
    setCargandoImagen(false);
  }, []);

  const cerrarModal = useCallback(() => {
    statusRevision.current += 1;
    activeStatus.current = null;
    setModalAbierto(false);
    setDetalleCamara(null);
    setErrorConsulta(null);
    setLoading((prev) => ({ ...prev, cameraStatus: false }));
    limpiarImagen();
  }, [limpiarImagen]);

  const loadComercios = useCallback(async () => {
    const revision = ++listRevision.current;
    setLoading((prev) => ({ ...prev, initial: true }));
    setError(null);
    try {
      const result = await getCamerasListService();
      if (!mounted.current || revision !== listRevision.current) return null;
      if (result.success) {
        catalog.current = result.data.flatMap((group) => group.cameras);
        setComercios(result.data);
        setLastUpdated(new Date().toISOString());
      } else setError(result.error);
      return result;
    } catch (err) {
      if (!mounted.current || revision !== listRevision.current) return null;
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      if (mounted.current && revision === listRevision.current) {
        setLoading((prev) => ({ ...prev, initial: false }));
      }
    }
  }, []);

  const consultarEstadoCamara = useCallback(
    (ip) => {
      if (!ip) return Promise.resolve({ success: false, error: "Seleccione una cámara con IP." });
      if (activeStatus.current?.ip === ip) return activeStatus.current.promise;
      const revision = ++statusRevision.current;
      const camera = catalog.current.find((item) => item.ip === ip) || { ip };
      limpiarImagen();
      setDetalleCamara(camera);
      setModalAbierto(true);
      setErrorConsulta(null);
      setLoading((prev) => ({ ...prev, cameraStatus: true }));
      const promise = (async () => {
        try {
          const result = await getCameraStatusService(ip);
          if (!mounted.current || revision !== statusRevision.current) return null;
          if (result.success) setDetalleCamara({ ...camera, ...result.data });
          else setErrorConsulta(result.error || "No se pudo consultar el estado.");
          return result;
        } catch (err) {
          if (!mounted.current || revision !== statusRevision.current) return null;
          setErrorConsulta(err.message);
          return { success: false, error: err.message };
        } finally {
          if (mounted.current && revision === statusRevision.current) {
            activeStatus.current = null;
            setLoading((prev) => ({ ...prev, cameraStatus: false }));
          }
        }
      })();
      activeStatus.current = { ip, promise };
      return promise;
    },
    [limpiarImagen]
  );

  const consultarImagenCamara = useCallback(
    (ip) => {
      if (!ip) return Promise.resolve({ success: false, error: "La cámara no tiene IP." });
      if (activeImage.current?.ip === ip) return activeImage.current.promise;
      limpiarImagen();
      const revision = imageRevision.current;
      setCargandoImagen(true);
      const promise = (async () => {
        try {
          const result = await getCameraPictureService(ip);
          if (!mounted.current || revision !== imageRevision.current) {
            revokeImageUrl(result?.data?.imageUrl);
            return null;
          }
          if (result.success && result.data?.imageUrl) {
            imageUrl.current = result.data.imageUrl;
            setImagenCamara({ url: result.data.imageUrl, timestamp: result.data.timestamp, ip });
            return result;
          }
          setErrorImagen(result.error || "No se pudo obtener la imagen.");
          return { ...result, success: false };
        } catch (err) {
          if (!mounted.current || revision !== imageRevision.current) return null;
          setErrorImagen(err.message);
          return { success: false, error: err.message };
        } finally {
          if (mounted.current && revision === imageRevision.current) {
            activeImage.current = null;
            setCargandoImagen(false);
          }
        }
      })();
      activeImage.current = { ip, promise };
      return promise;
    },
    [limpiarImagen]
  );

  const seleccionarComercio = useCallback(
    (commerce) => {
      cerrarModal();
      setComercioSeleccionado(commerce);
      setCamaraSeleccionada(null);
      setFiltros((prev) => ({ ...prev, comercio: commerce }));
    },
    [cerrarModal]
  );
  const seleccionarCamara = useCallback(
    (camera) => {
      cerrarModal();
      setCamaraSeleccionada(camera);
    },
    [cerrarModal]
  );
  const setFiltro = useCallback(
    (key, value) => setFiltros((prev) => ({ ...prev, [key]: value })),
    []
  );
  const clearFilters = useCallback(() => {
    cerrarModal();
    setComercioSeleccionado(null);
    setCamaraSeleccionada(null);
    setFiltros({ busqueda: "", comercio: null, estado: "todos", ip: "" });
  }, [cerrarModal]);
  const refreshData = useCallback(() => {
    cerrarModal();
    return loadComercios();
  }, [cerrarModal, loadComercios]);

  useEffect(() => {
    mounted.current = true;
    loadComercios();
    return () => {
      mounted.current = false;
      listRevision.current += 1;
      statusRevision.current += 1;
      imageRevision.current += 1;
      activeStatus.current = null;
      activeImage.current = null;
      revokeImageUrl(imageUrl.current);
    };
  }, [loadComercios]);

  const todasLasCamaras = useMemo(() => comercios.flatMap((group) => group.cameras), [comercios]);
  const camaras = useMemo(
    () => todasLasCamaras.filter((camera) => matchesCamera(camera, filtros)),
    [todasLasCamaras, filtros]
  );
  const stats = useMemo(
    () => ({
      totalCamaras: camaras.length,
      totalComercios: new Set(camaras.map((camera) => camera.commerceName)).size,
      disponibles: camaras.filter((camera) => camera.available === 1).length,
      sinAlcance: camaras.filter((camera) => camera.available === 2).length,
      desconocidas: camaras.filter((camera) => ![1, 2].includes(camera.available)).length,
    }),
    [camaras]
  );
  return {
    comercios,
    camaras,
    comercioSeleccionado,
    camaraSeleccionada,
    detalleCamara,
    modalAbierto,
    imagenCamara,
    cargandoImagen,
    errorImagen,
    loading,
    error,
    errorConsulta,
    lastUpdated,
    filtros,
    stats,
    seleccionarComercio,
    seleccionarCamara,
    setFiltro,
    clearFilters,
    consultarEstadoCamara,
    consultarImagenCamara,
    cerrarModal,
    refreshData,
    camarasDelComercioSeleccionado: todasLasCamaras.filter(
      (camera) => camera.commerceName === comercioSeleccionado?.name
    ),
  };
};
